import type { ReactElement } from 'react';

import Image from 'next/image';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { media } from '../styles';
import { FesLink } from '~/components/shared/core/link/FesLink';
import {
  buildPlayStoreUrl,
  getAndroidAppName,
  getAndroidPlayPackageId,
  isAndroidUserAgent,
} from '~/utilities/constants/androidAppBanner';
import { ANDROID_APP_BANNER_DISMISSED } from '~/utilities/constants/localStorageKeys';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { storageAvailable } from '~/utilities/helpers';

// Native Android "get the app" banner. Chrome shows no automatic install banner
// (removed in Chrome 76), and `prefer_related_applications` suppresses the PWA
// `beforeinstallprompt`, so this is a hand-rolled banner that deep-links to the
// Play Store. The iOS equivalent is the Safari Smart App Banner in `NativeAppBanner`.
type AndroidAppBannerProps = {
  // Copy is sourced in MasterLayout from the footer's GlobalAppStoreLinks content
  // (title/text/findOutMore), already localized. Falls back to the brand name +
  // English defaults when that content is absent.
  appName?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  closeLabel?: string;
};

const S = {
  Banner: styled.aside`
    ${({ theme }) => css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 505;
      display: flex;
      align-items: center;
      gap: ${theme.size[3]};
      box-sizing: border-box;
      padding: ${theme.size[2]} ${theme.size[3]};
      background-color: ${theme.colors.background.base};
      border-bottom: 1px solid ${theme.colors.border.subtle};

      @media ${media(theme).greaterThan('sm')} {
        display: none;
      }
    `}
  `,

  Icon: styled.span`
    flex: 0 0 auto;
    line-height: 0;
  `,

  Text: styled.span`
    ${({ theme }) => css`
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      color: ${theme.colors.content.primary};
    `}
  `,

  Title: styled.span`
    ${({ theme }) => css`
      font-size: ${theme.font.size[2]};
      font-weight: 700;
    `}
  `,

  Description: styled.span`
    ${({ theme }) => css`
      font-size: ${theme.font.size[1]};
    `}
  `,

  Cta: styled(FesLink)`
    ${({ theme }) => css`
      flex: 0 0 auto;
      padding: ${theme.size[2]} ${theme.size[3]};
      font-size: ${theme.font.size[2]};
      font-weight: 700;
      text-transform: uppercase;
      color: ${theme.colors.content.primary};
      white-space: nowrap;
    `}
  `,

  Close: styled.button`
    ${({ theme }) => css`
      flex: 0 0 auto;
      padding: ${theme.size[2]};
      background: none;
      border: none;
      cursor: pointer;
      font-size: ${theme.font.size[3]};
      line-height: 1;
      color: ${theme.colors.content.primary};
    `}
  `,
};

const AndroidAppBannerComponent = ({
  appName,
  title,
  description = 'Shop faster in the app.',
  ctaLabel = 'View',
  closeLabel = 'Close',
}: AndroidAppBannerProps): ReactElement | null => {
  const { siteType } = useAppContext();
  const bannerRef = useRef<HTMLElement>(null);
  const [isAndroid, setIsAndroid] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const packageId = getAndroidPlayPackageId(siteType);
  const resolvedAppName = appName || getAndroidAppName(siteType);
  const resolvedTitle = title || resolvedAppName;
  const folderPath = siteType === SiteType.Aaf ? SiteType.Aaf : SiteType.Gstar;
  const visible = isAndroid && !dismissed && !!packageId;

  useEffect(() => {
    setIsAndroid(isAndroidUserAgent(window.navigator.userAgent));

    if (storageAvailable('localStorage')) {
      setDismissed(localStorage.getItem(ANDROID_APP_BANNER_DISMISSED) === 'true');
    }
  }, []);

  // Keep --smart-banner-height in sync with the rendered height so the top
  // navigation (top: var(--smart-banner-height)) and content padding offset correctly.
  const syncBannerHeight = useCallback(() => {
    const height = visible && bannerRef.current ? bannerRef.current.offsetHeight : 0;

    document.documentElement.style.setProperty('--smart-banner-height', `${height}px`);
  }, [visible]);

  useEffect(() => {
    syncBannerHeight();
    window.addEventListener('resize', syncBannerHeight);

    return () => {
      window.removeEventListener('resize', syncBannerHeight);
      document.documentElement.style.setProperty('--smart-banner-height', '0px');
    };
  }, [syncBannerHeight]);

  const handleClose = useCallback(() => {
    if (storageAvailable('localStorage')) {
      localStorage.setItem(ANDROID_APP_BANNER_DISMISSED, 'true');
    }

    setDismissed(true);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <S.Banner ref={bannerRef} aria-label={resolvedAppName} data-testid="android-app-banner">
      <S.Icon>
        <Image
          src={`/_fes/${folderPath}/android-chrome-192x192.png`}
          alt=""
          role="presentation"
          width={40}
          height={40}
        />
      </S.Icon>
      <S.Text>
        <S.Title>{resolvedTitle}</S.Title>
        <S.Description>{description}</S.Description>
      </S.Text>
      <S.Cta href={buildPlayStoreUrl(packageId)} data-testid="android-app-banner-cta">
        {ctaLabel}
      </S.Cta>
      <S.Close
        type="button"
        aria-label={closeLabel}
        onClick={handleClose}
        data-testid="android-app-banner-close"
      >
        &times;
      </S.Close>
    </S.Banner>
  );
};

export const AndroidAppBanner = memo(AndroidAppBannerComponent);
